"use client";

/**
 * Clickable video poster — the only thing that loads before the visitor asks
 * to watch. A `<picture>` (AVIF → WebP → JPG) with a fixed aspect ratio (no
 * layout shift), a real `<button>` with a visible play affordance, the title
 * and the duration badge. The video itself is NOT mounted here, so no media
 * bytes are fetched until the visitor clicks (the poster is the section's LCP
 * candidate, never the video).
 */

import { Play } from "lucide-react";
import { formatDuration, type MarketingVideo } from "@/lib/marketing-videos";

export function VideoPoster({
  video,
  onOpen,
  ariaLabel,
  caption,
  priority = false,
  buttonRef,
}: {
  video: MarketingVideo;
  onOpen: () => void;
  /** Full accessible label, e.g. "Bekijk FixControl in 60 seconden (video, 1:06)". */
  ariaLabel: string;
  /** Short visible line under the play button (plain language). */
  caption?: string;
  /** Set for above-the-fold posters so the browser prioritizes the image. */
  priority?: boolean;
  /** Focus is returned here when the modal closes. */
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
}) {
  if (!video.poster) return null;
  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={onOpen}
      aria-label={ariaLabel}
      className="group relative block w-full overflow-hidden rounded-2xl border border-line-subtle bg-bg-raised text-left shadow-xl transition hover:border-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base"
    >
      <span className="block aspect-video w-full">
        <picture>
          <source srcSet={video.poster.avif} type="image/avif" />
          <source srcSet={video.poster.webp} type="image/webp" />
          {/* Plain <img>: the poster is a fixed-size asset from public/,
              already AVIF/WebP-optimized by the sync tool. */}
          <img
            src={video.poster.jpg}
            alt=""
            width={video.width}
            height={video.height}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            className="block h-full w-full object-cover"
          />
        </picture>
      </span>

      {/* Play affordance + duration badge (not hover-only — always visible).
          No darkening scrim over the frame: the poster stays as bright as the
          video itself, only the button carries contrast. */}
      <span aria-hidden className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-grad text-white shadow-glow transition group-hover:scale-105 md:h-20 md:w-20">
          <Play size={28} fill="currentColor" className="ml-1" />
        </span>
      </span>
      <span
        aria-hidden
        className="absolute bottom-3 right-3 rounded-md border border-line-subtle bg-bg-base/80 px-2 py-0.5 font-mono text-[12px] text-fg backdrop-blur"
      >
        {formatDuration(video.durationSeconds)}
      </span>
      {caption && (
        <span
          aria-hidden
          className="absolute bottom-3 left-3 max-w-[70%] rounded-md border border-line-subtle bg-bg-base/80 px-2.5 py-1 text-[13px] font-medium text-fg-bright backdrop-blur"
        >
          {caption}
        </span>
      )}
    </button>
  );
}
